// src/pages/AuthPage/ResetPassword.jsx
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useToast } from '../../components/Toaster/use-toast';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { resetPassword } from '../../api/userApi';
import '../AuthPage/AuthCards.css';
import Input from '../../components/Input/Input';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const location = useLocation();
  const { uid, token } = useMemo(
    () => ({ uid: params.get('uid') || '', token: params.get('token') || '' }),
    [params]
  );

  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  // track "touched" so errors only show after interaction
  const [touched, setTouched] = useState({ password: false, confirm: false });

  const { toast } = useToast();
  const navigate = useNavigate();

  const paramsValid = !!uid && !!token;

  // field-level errors
  const pwdError =
    password && password.length < 8 ? 'Password must be at least 8 characters.' : '';
  const confirmError =
    confirm && confirm !== password ? 'Passwords do not match.' : '';

  const showPwdError     = touched.password && !!pwdError;
  const showConfirmError = touched.confirm  && !!confirmError;

  const formValid = paramsValid && password.length >= 8 && password === confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    // mark as touched to surface errors if user clicks submit early
    setTouched({ password: true, confirm: true });
    if (!formValid) return;

    try {
      setSubmitting(true);
      await resetPassword({ userId: Number(uid), token, password });
      toast({
        title: 'Password updated',
        description: 'You can now sign in with your new password.',
        variant: 'success',
      });
      navigate('/auth', { replace: true });
    } catch (err) {
      toast({
        title: 'Reset link invalid or expired',
        description: 'Request a new link and try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card-head">
          <h1 className="auth-title">Create a New Password</h1>
          <p className="auth-subtitle">Use at least 8 characters for a strong password.</p>
        </div>

        {!paramsValid ? (
          <div className="auth-warning">
            The reset link is missing or invalid. Please request a new one.
            <div style={{ marginTop: 12 }}>
              <Link to="/forgot-password" className="auth-btn primary" style={{ display: 'inline-block' }}>
                Get a new link
              </Link>
            </div>
          </div>
        ) : (
          <form className="auth-form" onSubmit={onSubmit} noValidate>
            {/* New Password */}
            <label className="auth-label" htmlFor="password">New Password</label>
      
            <Input
  id="password"
  type="password"           // ← reveal comes from Input now
  placeholder="Enter new password"
  value={password}
  minLength={8}
  onChange={(e) => setPassword(e.target.value)}
  className="auth-input"
/>

            {/* Confirm Password */}
            <label className="auth-label" htmlFor="confirm">Confirm Password</label>
            <Input
  id="confirm"
  type="password"           // ← reveal comes from Input now
  placeholder="Confirm password"
  value={confirm}
  minLength={8}
  onChange={(e) => setConfirm(e.target.value)}
  className="auth-input"
/>

            <button className="auth-btn primary" disabled={!formValid || submitting}>
              {submitting ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        )}

        <div className="auth-footer-row">
          <Link to="/auth" className="link-inline">
            <ArrowLeft size={16} style={{ marginRight: 6 }} />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
